import React, { useState } from "react";
import "./login.css";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiShield } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { authenticateUser } from "../../redux/authSlice";
import Logo from "../../components/logo/logo";

const Login = () => {
  const dispatch = useAppDispatch();
  const isSubmitting = useAppSelector(
    (state) => state.authentication.isProcessingRequest
  );
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: yup.object({
      email: yup
        .string()
        .trim()
        .email("Enter a valid email address")
        .required("Email address is required"),
      password: yup
        .string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
    }),
    onSubmit: (values) => {
      dispatch(
        authenticateUser({ email: values.email.trim(), password: values.password })
      );
    },
  });

  const handleForgotPassword = (e) => {
    e.preventDefault();
    toast.info("Please contact your system administrator to reset your password.");
  };

  return (
    <div className="admin-login">
      <div className="admin-login__card">
        <div className="admin-login__logo">
          <Logo />
        </div>

        <h1 className="admin-login__title">Welcome Back</h1>
        <p className="admin-login__subtitle">
          Sign in to access the TreatMyTravel admin portal
        </p>

        <form className="admin-login__form" onSubmit={formik.handleSubmit} noValidate>
          <div className="admin-login__field">
            <label htmlFor="email">Email Address</label>
            <div
              className={`admin-login__input${
                formik.touched.email && formik.errors.email ? " is-invalid" : ""
              }`}
            >
              <FiMail className="admin-login__icon" aria-hidden="true" />
              <input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="admin@treatmytravel.com"
                disabled={isSubmitting}
                {...formik.getFieldProps("email")}
              />
            </div>
            {formik.touched.email && formik.errors.email ? (
              <p className="admin-login__error">{formik.errors.email}</p>
            ) : null}
          </div>

          <div className="admin-login__field">
            <div className="admin-login__field-header">
              <label htmlFor="password">Password</label>
              <button
                type="button"
                className="admin-login__link"
                onClick={handleForgotPassword}
              >
                Forgot password?
              </button>
            </div>
            <div
              className={`admin-login__input${
                formik.touched.password && formik.errors.password ? " is-invalid" : ""
              }`}
            >
              <FiLock className="admin-login__icon" aria-hidden="true" />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                disabled={isSubmitting}
                {...formik.getFieldProps("password")}
              />
              <button
                type="button"
                className="admin-login__toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {formik.touched.password && formik.errors.password ? (
              <p className="admin-login__error">{formik.errors.password}</p>
            ) : null}
          </div>

          <button type="submit" className="admin-login__submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="admin-login__spinner" aria-hidden="true" />
            ) : (
              <>
                Sign In <FiArrowRight />
              </>
            )}
          </button>
        </form>

        <p className="admin-login__footer">
          <FiShield aria-hidden="true" /> Secure admin access &bull; TreatMyTravel
        </p>
      </div>
    </div>
  );
};

export default Login;
